import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { getSecrets } from "../../config";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useBlockList } from "../../hooks/useBlockList";
import { useWhiteboardRoom } from "../../hooks/useWhiteboardRoom";
import {
  setChangeUserAddress,
  setIfConnected,
  setNumUsers,
  setRoomState,
} from "../../store/reducers/whiteboard";
import { getProticoClient } from "../../utils/api";
import { TPreviewMessage, TWhiteboardMessage } from "protico-sdk";
import { useViewport } from "../../hooks/useViewport";

export interface IMessageReference {
  root_id: string;
  message_id: string;
  /** the address of the sender */
  sender_address: string;
  messageText: string;
}

interface IPendingReactionDelta {
  delta: number;
  desiredUserReacted: boolean;
  mutationId: string;
}

interface IWhiteboardWebsocketContext {
  handleSendMessage: (text: string, tag: string, point?: number) => void;
  handleNewList: (
    text: string,
    data: any,
    sign: any,
    hash: any,
    chainId: string
  ) => void;
  readyState: ReadyState;
  handleSetNextReference: (message_reference: IMessageReference | null) => void;
  nextReference: IMessageReference | null;
  displayLatestMessage: TWhiteboardMessage;
  shouldScrollToBottom: boolean;
  cleanShouldScrollToBottom: () => void;
  handleSetShouldScrollToBottom: () => void;
  handleSetMessageList: (newMessageList: any[]) => void;
  handleSetThreadMessageList: (newMessageList: any[], rootId: string) => void;
  handleNewMessages: (newMessageList: any[], rootId?: string) => void;
  isLatestMessageExist: boolean;
  displayMessageList: any[];
  setMessageList: React.Dispatch<React.SetStateAction<TWhiteboardMessage[]>>;
  applyOptimisticReaction: (
    messageId: string,
    reaction: string,
    mutationId: string
  ) => void;
  rollbackOptimisticReaction: (
    messageId: string,
    reaction: string,
    mutationId?: string
  ) => void;
  messageList: any[];
  isMessageLoading: boolean;
  setIsMessageLoading: (loading: boolean) => void;
  isInitialMessageLoading: boolean;
  setIsInitialMessageLoading: (loading: boolean) => void;
  handleMsgPreview: (preivewMessage: TPreviewMessage) => void;
  errorImage: boolean;
  errorImageSize: boolean;
  setErrorImageSize: (error: boolean) => void;
  typingUser: string | null;
  setTypingUser: (user: string | null) => void;
}

export const WhiteboardWebsocketContext =
  React.createContext<IWhiteboardWebsocketContext>({
    handleSendMessage: () => {},
    handleNewList: () => {},
    readyState: ReadyState.CONNECTING,
    handleSetNextReference: () => {},
    nextReference: null,
    displayLatestMessage: null,
    shouldScrollToBottom: false,
    cleanShouldScrollToBottom: () => {},
    handleSetShouldScrollToBottom: () => {},
    handleSetMessageList: () => {},
    handleSetThreadMessageList: () => {},
    handleNewMessages: () => {},
    isLatestMessageExist: false,
    displayMessageList: [],
    setMessageList: () => {},
    applyOptimisticReaction: () => {},
    rollbackOptimisticReaction: () => {},
    messageList: [],
    isMessageLoading: true,
    setIsMessageLoading: () => {},
    isInitialMessageLoading: true,
    setIsInitialMessageLoading: () => {},
    handleMsgPreview: () => {},
    errorImage: false,
    errorImageSize: false,
    setErrorImageSize: () => {},
    typingUser: null,
    setTypingUser: () => {},
  });

export const WhiteboardWebsocketProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  //Public API that will echo messages sent to it back to the client
  const { NODE_ENV } = getSecrets();
  const { room, roomDomain, roomUrl } = useWhiteboardRoom();
  const { currentAddress } = useAppSelector((state) => state.auth);
  const uuid = getProticoClient().userInfo?.uuid;
  const { isStale } = useViewport();
  const [socketUrl, setSocketUrl] = useState(null);
  const [nextReference, setMessageReference] =
    useState<IMessageReference | null>(null);
  const { lastMessage, readyState } = useWebSocket(socketUrl, {
    // onOpen: () => console.log("whiteboard socket opened"),
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: (closeEvent) => true,
    reconnectAttempts: 10,
    //attemptNumber will be 0 the first time it attempts to reconnect, so this equation results in a reconnect pattern of 1 second, 2 seconds, 4 seconds, 8 seconds, and then caps at 10 seconds until the maximum number of attempts is reached
    reconnectInterval: (attemptNumber) =>
      Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
  });
  const { isScrollAbove } = useAppSelector((state) => state.whiteboard);
  const dispatch = useAppDispatch();

  const [displayLatestMessage, setDisplayLatestMessage] =
    useState<TWhiteboardMessage>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] =
    useState<boolean>(false);
  const [errorImage, setErrorImage] = useState<boolean>(false);
  const [errorImageSize, setErrorImageSize] = useState<boolean>(false);

  const { blockList } = useBlockList();

  const [messageList, setMessageList] = useState<TWhiteboardMessage[]>([]);

  const pendingReactionDeltas = useRef<
    Map<string, Map<string, IPendingReactionDelta>>
  >(new Map());
  const mutationLookup = useRef<
    Map<string, { messageId: string; reaction: string }>
  >(new Map());
  const [pendingRevision, setPendingRevision] = useState(0);

  const findMessageById = useCallback(
    (messageId: string): TWhiteboardMessage | null => {
      for (const msg of messageList) {
        if (msg.id === messageId) return msg;
        if (msg.thread_preview) {
          const threadMsg = msg.thread_preview.find(
            (thread) => thread.id === messageId
          );
          if (threadMsg) return threadMsg;
        }
      }
      return null;
    },
    [messageList]
  );

  const applyReactionDeltasToMessage = (
    message: TWhiteboardMessage
  ): TWhiteboardMessage => {
    let nextReactions = message.reactions
      ? message.reactions.map((reaction) => ({ ...reaction }))
      : [];
    const deltas = pendingReactionDeltas.current.get(message.id);

    if (deltas && deltas.size) {
      const reactionMap = new Map<string, any>();
      nextReactions.forEach((reaction) =>
        reactionMap.set(reaction.reaction, { ...reaction })
      );

      deltas.forEach((entry, reactionName) => {
        const current = reactionMap.get(reactionName);
        const baseCount = current?.count ?? 0;
        const newCount = Math.max(baseCount + entry.delta, 0);

        if (newCount <= 0) {
          reactionMap.delete(reactionName);
        } else {
          reactionMap.set(reactionName, {
            reaction: reactionName,
            count: newCount,
            user_reacted: entry.desiredUserReacted,
          });
        }
      });

      nextReactions = Array.from(reactionMap.values());
    }

    let nextThreadPreview = message.thread_preview;
    if (message.thread_preview) {
      nextThreadPreview = message.thread_preview.map((threadMsg) =>
        applyReactionDeltasToMessage(threadMsg)
      );
    }

    return {
      ...message,
      reactions: nextReactions,
      thread_preview: nextThreadPreview,
    };
  };

  const applyOptimisticReaction = useCallback(
    (messageId: string, reactionName: string, mutationId: string) => {
      const targetMessage = findMessageById(messageId);
      const serverReaction = targetMessage?.reactions?.find(
        (reaction) => reaction.reaction === reactionName
      );
      const serverUserReacted = serverReaction?.user_reacted ?? false;

      const deltasForMessage =
        pendingReactionDeltas.current.get(messageId) ?? new Map();
      const existingEntry = deltasForMessage.get(reactionName);
      const currentDesired = existingEntry
        ? existingEntry.desiredUserReacted
        : serverUserReacted;
      const newDesired = !currentDesired;
      const newDelta =
        (newDesired ? 1 : 0) - (serverUserReacted ? 1 : 0);

      if (newDelta === 0) {
        deltasForMessage.delete(reactionName);
        if (deltasForMessage.size === 0) {
          pendingReactionDeltas.current.delete(messageId);
        } else {
          pendingReactionDeltas.current.set(messageId, deltasForMessage);
        }
      } else {
        deltasForMessage.set(reactionName, {
          desiredUserReacted: newDesired,
          delta: newDelta,
          mutationId,
        });
        pendingReactionDeltas.current.set(messageId, deltasForMessage);
      }

      mutationLookup.current.set(mutationId, { messageId, reaction: reactionName });
      setPendingRevision((revision) => revision + 1);
    },
    [findMessageById]
  );

  const rollbackOptimisticReaction = useCallback(
    (messageId: string, reactionName: string, mutationId?: string) => {
      const deltasForMessage = pendingReactionDeltas.current.get(messageId);
      if (!deltasForMessage) return;
      const entry = deltasForMessage.get(reactionName);
      if (!entry) return;
      if (mutationId && entry.mutationId !== mutationId) return;

      deltasForMessage.delete(reactionName);
      if (deltasForMessage.size === 0) {
        pendingReactionDeltas.current.delete(messageId);
      } else {
        pendingReactionDeltas.current.set(messageId, deltasForMessage);
      }
      if (mutationId) {
        mutationLookup.current.delete(mutationId);
      }
      setPendingRevision((revision) => revision + 1);
    },
    []
  );

  const acknowledgePendingReaction = useCallback((mutationId?: string) => {
    if (!mutationId) return;
    const lookup = mutationLookup.current.get(mutationId);
    mutationLookup.current.delete(mutationId);
    if (!lookup) return;

    const deltasForMessage = pendingReactionDeltas.current.get(lookup.messageId);
    if (!deltasForMessage) return;

    const entry = deltasForMessage.get(lookup.reaction);
    if (!entry || entry.mutationId !== mutationId) return;

    deltasForMessage.delete(lookup.reaction);
    if (deltasForMessage.size === 0) {
      pendingReactionDeltas.current.delete(lookup.messageId);
    } else {
      pendingReactionDeltas.current.set(lookup.messageId, deltasForMessage);
    }
    setPendingRevision((revision) => revision + 1);
  }, []);

  const [isMessageLoading, setIsMessageLoading] = useState<boolean>(true);
  const [isInitialMessageLoading, setIsInitialMessageLoading] =
    useState<boolean>(true);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const displayMessageList = useMemo(() => {
    // don't block user's message
    const blocklistWhitoutUser = blockList.filter(
      (item) => item !== currentAddress
    );
    const filtered = messageList.filter((item) => {
      //if item.thread_preview is not null, it is a thread message, so we should also filter it
      if (item.thread_preview) {
        item.thread_preview = item.thread_preview.filter(
          (thread_item) =>
            !blocklistWhitoutUser.includes(thread_item.sender_meta?.address) &&
            !thread_item.hidden &&
            (!thread_item.shadow_hidden ||
              thread_item.sender_meta?.address === currentAddress ||
              thread_item.sender_meta?.uuid === uuid)
        );
      }
      return (
        !blocklistWhitoutUser.includes(item.sender_meta?.address) &&
        !item.hidden &&
        (!item.shadow_hidden ||
          item.sender_meta?.address === currentAddress ||
          item.sender_meta?.uuid === uuid)
      );
    });
    return filtered.map((message) => applyReactionDeltasToMessage(message));
  }, [
    JSON.stringify(messageList),
    JSON.stringify(blockList),
    // isAllowGuest,
    currentAddress,
    pendingRevision,
  ]);
  const [latestMessageseen, setLatestMessageseen] = useState<any>(null);
  const [isLatestMessageExist, setIsLatestMessageExist] =
    useState<boolean>(true);

  useEffect(() => {
    if (!room || !roomDomain) return;
    if (isStale) {
      setSocketUrl(null);
      return;
    }
    setSocketUrl(
      (NODE_ENV === "production" ? "wss://" : "ws://") +
        window.location.host +
        "/ws/websocket/" +
        roomDomain +
        "/" +
        room +
        "/" +
        (currentAddress.length > 0 ? currentAddress : "Guest")
    );
  }, [roomDomain, room, currentAddress, isStale]);

  useEffect(() => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data);
      if (data.type === "user_count_change") {
        dispatch(setNumUsers(data.count));
        if (data.action === "leave") {
          dispatch(setRoomState("FlashChat_Message_User_Out"));
          dispatch(setChangeUserAddress(data.user || "Guest"));
        } else if (data.action === "join") {
          dispatch(setRoomState("FlashChat_Message_User_In"));
          dispatch(setChangeUserAddress(data.user || "Guest"));
        }
      }
      if (data.type === "new_message" || data.type === "new_list") {
        if (isLatestMessageExist) {
          handleNewMessages([data.payload], data.payload.root_id);
          if (!isScrollAbove) setShouldScrollToBottom(true);
        }
        if (isScrollAbove || !isLatestMessageExist)
          setDisplayLatestMessage(data.payload);
      }
      if (data.type === "image_error") {
        handleImageError(data.payload.imageUrl, data.payload.rootId);
      }
      if (data.type === "nftlist_available") {
        updateNFTMessage(data);
      }
      if (data.type === "reaction") {
        updateMsgReaction(data, data.payload.root_id);
      }
    }
  }, [lastMessage]);
  useEffect(() => {
    // TODO: remove this, we should check state by readyState only
    if (readyState === ReadyState.OPEN) dispatch(setIfConnected(true));
    else dispatch(setIfConnected(false));
  }, [readyState]);

  const postSendMessage = useCallback(
    (data: any, room: string, roomDomain: string, roomUrl: string) =>
      getProticoClient().whiteboard.postMessage(
        data,
        room,
        roomDomain,
        roomUrl
      ),
    [currentAddress]
  );

  const handleSendMessage = useCallback(
    (text: string, tag: string, point?: number) => {
      const payloadDict = {
        messageText: text,
        tag: tag,
        superChatPoint: point,
      } as any;
      if (nextReference && nextReference.root_id) {
        payloadDict.message_reference = {
          message_id: nextReference.message_id,
          root_id: nextReference.root_id,
        };
      }
      const payload = payloadDict as unknown as any;
      postSendMessage(
        { payload, type: "new_message" },
        room,
        roomDomain,
        roomUrl
      );
      setMessageReference(null);
    },
    [nextReference, postSendMessage, room, roomDomain, roomUrl]
  );

  const handleSetNextReference = useCallback(
    (message_reference: IMessageReference | null) => {
      setMessageReference(message_reference);
    },
    []
  );

  const handleNewList = useCallback(
    (text: string, data: any, sign: any, hash: any, chainId: string) => {
      postSendMessage(
        {
          type: "new_list",
          payload: {
            messageText: text,
          },
          typeddata: {
            data: data,
            sign: sign,
            hash: hash,
            chainId: chainId,
          },
        },
        room,
        roomDomain,
        roomUrl
      );
      setMessageReference(null);
    },
    [postSendMessage, room, roomDomain]
  );
  const cleanShouldScrollToBottom = useCallback(() => {
    setShouldScrollToBottom(false);
  }, []);
  const handleSetShouldScrollToBottom = useCallback(() => {
    setShouldScrollToBottom(true);
  }, []);
  const cleanDisplayLatestMessage = useCallback(() => {
    setDisplayLatestMessage(null);
  }, []);

  const getTimeFromCreatedAt = (message: TWhiteboardMessage) =>
    new Date(message.createdAt).getTime();

  const isNewMessage = (
    latestMessageseen: TWhiteboardMessage,
    newMessage: TWhiteboardMessage
  ) => {
    return (
      latestMessageseen === null ||
      getTimeFromCreatedAt(newMessage) > getTimeFromCreatedAt(latestMessageseen)
    );
  };

  const handleSetMessageList = useCallback(
    (newMessageList: TWhiteboardMessage[]) => {
      setMessageList(newMessageList);
      if (
        newMessageList.length > 0 &&
        isNewMessage(latestMessageseen, newMessageList[0])
      ) {
        setLatestMessageseen(newMessageList[0]);
      }
      setIsMessageLoading(false);
      setIsInitialMessageLoading(false);
    },
    [latestMessageseen?.id]
  );

  const handleSetThreadMessageList = useCallback(
    (newMessageList: TWhiteboardMessage[], rootId: string) => {
      //find the id = rootId in the messageList, and update the thread_preview
      setMessageList((prev) =>
        prev.map((msg) => {
          if (msg.id === rootId) {
            return {
              ...msg, // keep all other properties of the message object
              thread_count: msg.thread_count - newMessageList.length,
              thread_preview: newMessageList, // update the text property with a new value
            };
          }
          return msg;
        })
      );
      if (
        newMessageList.length > 0 &&
        isNewMessage(latestMessageseen, newMessageList[0])
      ) {
        setLatestMessageseen(newMessageList[0]);
      }
    },
    [JSON.stringify(messageList), latestMessageseen?.id]
  );

  const getUniqueMessages = (
    newMessageList: TWhiteboardMessage[],
    currentMessageList: TWhiteboardMessage[]
  ) => {
    const seen = new Set(currentMessageList.map((msg) => msg.id));
    return newMessageList.filter((msg) => !seen.has(msg.id));
  };

  const sortMessages = (messageList: TWhiteboardMessage[]) => {
    return messageList.sort(
      (a, b) => getTimeFromCreatedAt(b) - getTimeFromCreatedAt(a)
    );
  };

  const handleImageError = (message_text: string, root_id: string) => {
    //check if the message_text is in the messageList
    const messageIndex = messageList.findIndex(
      (msg) => msg.messageText === message_text
    );
    if (messageIndex !== -1) {
      setErrorImage(true);
      return;
      // setTimeout(() => {
      //   setErrorImage(false);
      // }, 5000);
    }
    //check if the root_id is in the messageList
    const rootMessageIndex = messageList.findIndex((msg) => msg.id === root_id);
    if (rootMessageIndex !== -1) {
      const threadIndex = messageList[
        rootMessageIndex
      ].thread_preview.findIndex((msg) => msg.messageText === message_text);
      if (threadIndex !== -1) {
        setErrorImage(true);
        return;
      }
    }
  };

  const handleNewMessages = useCallback(
    (newMessageList: TWhiteboardMessage[], rootId?: string) => {
      const uniqueMessages = getUniqueMessages(newMessageList, messageList);
      const combinedMessageList = uniqueMessages.reduce(
        (acc, newMessage) => {
          const isThread = newMessage.referenced_message !== null;
          if (rootId && isThread) {
            const existingMessageIndex = acc.findIndex(
              (msg) => msg.id === rootId
            );

            if (existingMessageIndex !== -1) {
              if (acc[existingMessageIndex].thread_preview) {
                const threadPreviewIndex = acc[
                  existingMessageIndex
                ].thread_preview.findIndex(
                  (threadmsg) =>
                    threadmsg.messageText === newMessage.messageText &&
                    threadmsg.isSended === false
                );
                if (threadPreviewIndex !== -1) {
                  acc[existingMessageIndex].thread_preview[threadPreviewIndex] =
                    newMessage;
                } else {
                  acc[existingMessageIndex].thread_preview = sortMessages([
                    ...acc[existingMessageIndex].thread_preview,
                    newMessage,
                  ]);
                }
              } else {
                acc[existingMessageIndex].thread_preview = [newMessage];
              }
            }
          } else {
            const existingMessageIndex = acc.findIndex(
              (msg) =>
                msg.messageText === newMessage.messageText &&
                msg.isSended === false
            );
            if (existingMessageIndex !== -1) {
              acc[existingMessageIndex] = newMessage;
            } else {
              acc.push(newMessage);
            }
          }

          return acc;
        },
        [...messageList]
      );
      const newList = sortMessages([...combinedMessageList]);

      setMessageList(newList);
      if (
        newList.length > 0 &&
        (isNewMessage(latestMessageseen, newList[0]) ||
          newList[0].thread_preview)
      ) {
        setLatestMessageseen(newList[0]);
      }
    },
    [JSON.stringify(messageList), latestMessageseen?.id]
  );

  const updateNFTMessage = useCallback(
    (nftMessage: any) => {
      setMessageList((prev) =>
        prev.map((msg) => {
          if (msg.id === nftMessage.message_id) {
            return {
              ...msg, // keep all other properties of the message object
              available: nftMessage.available, // update the text property with a new value
              item_status: nftMessage.item_status, // update the text property with a new value
            };
          }
          return msg;
        })
      );
    },
    [JSON.stringify(messageList)]
  );

  const updateMsgReaction = useCallback(
    (reactionMessage: any, rootId?: string) => {
      const updatedMessagePayload = reactionMessage?.payload;
      if (!updatedMessagePayload) return;

      setMessageList((prevList) => {
        const messageIdToUpdate = updatedMessagePayload.id;
        if (updatedMessagePayload.mutation_id) {
          acknowledgePendingReaction(updatedMessagePayload.mutation_id);
        }

        // --- Step 1: Find the message to update ---
        let messageIndex: number;
        let isThreadUpdate = false;
        let rootMessageIndex = -1;

        if (rootId) {
          rootMessageIndex = prevList.findIndex((msg) => msg.id === rootId);
          messageIndex =
            rootMessageIndex !== -1
              ? prevList[rootMessageIndex].thread_preview?.findIndex(
                  (tm) => tm.id === messageIdToUpdate
                ) ?? -1
              : -1;
          isThreadUpdate = true;
        } else {
          messageIndex = prevList.findIndex(
            (msg) => msg.id === messageIdToUpdate
          );
        }

        // If the message is not found anywhere, do nothing.
        if (messageIndex === -1) {
          return prevList;
        }

        const newList = [...prevList];

        // --- Step 2: Define the intelligent update logic ---
        const applyUpdate = (messageToUpdate: TWhiteboardMessage) => {
          return {
            ...messageToUpdate,
            reactions: updatedMessagePayload.reactions,
          };
        };

        // --- Step 3: Apply the update to the correct message in the list ---
        if (
          isThreadUpdate &&
          rootMessageIndex !== -1 &&
          newList[rootMessageIndex].thread_preview
        ) {
          // It's a thread message
          const newThreadPreview = [
            ...newList[rootMessageIndex].thread_preview!,
          ];
          newThreadPreview[messageIndex] = applyUpdate(
            newThreadPreview[messageIndex]
          );
          newList[rootMessageIndex] = {
            ...newList[rootMessageIndex],
            thread_preview: newThreadPreview,
          };
        } else if (!isThreadUpdate) {
          // It's a root message
          newList[messageIndex] = applyUpdate(newList[messageIndex]);
        }

        return newList;
      });
    },
    [acknowledgePendingReaction]
  );
  const handleMsgPreview = useCallback(
    (preivewMessage: TPreviewMessage) => {
      setMessageList((prev) =>
        prev.map((msg) => {
          if (msg.id === preivewMessage.message_id) {
            return {
              ...msg, // keep all other properties of the message object
              urlMetadata: preivewMessage, // update the text property with a new value
            };
          }
          if (msg.thread_preview) {
            const threadPreviewIndex = msg.thread_preview.findIndex(
              (threadmsg) => threadmsg.id === preivewMessage.message_id
            );
            if (threadPreviewIndex !== -1) {
              return {
                ...msg, // keep all other properties of the message object
                thread_preview: msg.thread_preview.map((threadmsg) => {
                  if (threadmsg.id === preivewMessage.message_id) {
                    return {
                      ...threadmsg, // keep all other properties of the message object
                      urlMetadata: preivewMessage, // update the text property with a new value
                    };
                  }
                  return threadmsg;
                }), // update the text property with a new value
              };
            }
          }
          return msg;
        })
      );
    },
    [JSON.stringify(messageList)]
  );

  useEffect(() => {
    if (messageList.length === 0) {
      setIsLatestMessageExist(true);
      return;
    }
    setIsLatestMessageExist(
      messageList.some((msg) => msg.id === latestMessageseen?.id)
    );
  }, [JSON.stringify(messageList), latestMessageseen?.id]);

  useEffect(() => {
    if (!isScrollAbove) {
      cleanDisplayLatestMessage();
    }
  }, [isScrollAbove]);

  return (
    <WhiteboardWebsocketContext.Provider
      value={{
        handleSendMessage,
        handleNewList,
        readyState,
        handleSetNextReference,
        nextReference,
        displayLatestMessage,
        shouldScrollToBottom,
        cleanShouldScrollToBottom,
        handleSetShouldScrollToBottom,
        handleSetMessageList,
        handleSetThreadMessageList,
        handleNewMessages,
        isLatestMessageExist,
        displayMessageList,
        messageList,
        setMessageList,
        applyOptimisticReaction,
        rollbackOptimisticReaction,
        isMessageLoading,
        setIsMessageLoading,
        isInitialMessageLoading,
        setIsInitialMessageLoading,
        handleMsgPreview,
        errorImage,
        errorImageSize,
        setErrorImageSize,
        typingUser,
        setTypingUser,
      }}
    >
      {children}
    </WhiteboardWebsocketContext.Provider>
  );
};
